import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CalendarOff,
  Check,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { ConfirmModal } from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import type { RootState } from '../store';
import { apiClient } from '../utils/api';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { DateInput } from '../components/DateInput';
import { DateTimeInput } from '../components/DateTimeInput';

const DEFAULT_TIMEZONE = 'Europe/Paris';

interface TimeOffRequest {
  id: string;

  /*
   * Identité principale du demandeur, quel que soit son type de compte.
   */
  requesterId?: string | null;
  requesterType?: string | null;
  requesterRole?: string | null;
  requesterName?: string | null;

  /*
   * Références métier optionnelles, uniquement quand elles existent.
   */
  practitionerId?: string | null;
  employeeId?: string | null;

  /*
   * Champ historique : anciennes demandes enregistrées uniquement
   * avec un practitionerId.
   */
  practitionerName?: string | null;

  shopId: string;
  shopName: string;
  startDateTime: string;
  endDateTime: string;
  reason: string;
  isApproved: boolean;
  approvedBy?: string;
  timezone: string;
  createdAt: string;
}

interface Shop {
  id: string;
  name: string;
  timezone: string;
}

/*
 * Personne autorisée à demander un congé.
 *
 * id         : identité principale (requesterId envoyé à l'API)
 * key        : clé unique pour l'interface (une même personne peut
 *              exister à la fois comme praticien et comme employé)
 * shops      : shops déjà connus grâce à l'endpoint de liste
 */
interface TimeOffRequester {
  key: string;
  id: string;
  name: string;
  type: string;
  role?: string;
  practitionerId?: string | null;
  employeeId?: string | null;
  shops?: Shop[];
}

interface UnlockedBy {
  id: string;
  name: string;
  role: string;
  type: string;

  /*
   * practitionerId / employeeId sont de véritables IDs métier,
   * contrairement à id qui est l'identifiant de l'acteur.
   */
  practitionerId?: string;
  employeeId?: string;
  shopId?: string;
}

interface LayoutOutletContext {
  unlockedBy: UnlockedBy | null;
}

const normalizeKey = (value?: string | null): string =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');

const REQUESTER_LABELS: Record<string, string> = {
  PRACTITIONER: 'Praticien',
  EMPLOYEE: 'Employé',
  VENDOR: 'Vendeur',
  SELLER: 'Vendeur',
  MANAGER: 'Manager',
  ADMIN: 'Administrateur',
  COMPANY_ADMIN: 'Administrateur',
  SHOP_ADMIN: 'Administrateur',
  SUPER_ADMIN: 'Super Admin',
};

/*
 * Libellé secondaire affiché sous le nom du demandeur.
 * Tout nouveau type de compte reste affiché sans code dédié.
 */
const formatRequesterRole = (
  type?: string | null,
  role?: string | null,
): string => {
  const normalizedRole = normalizeKey(role);
  const normalizedType = normalizeKey(type);

  const known =
    REQUESTER_LABELS[normalizedRole] ||
    REQUESTER_LABELS[normalizedType];

  if (known) {
    return known;
  }

  const fallback = normalizedRole || normalizedType;

  if (!fallback) {
    return '';
  }

  return fallback
    .toLocaleLowerCase('fr-FR')
    .replace(/_/g, ' ');
};

const requestRequesterName = (request: TimeOffRequest): string =>
  String(request.requesterName || '').trim() ||
  String(request.practitionerName || '').trim() ||
  'Utilisateur';

const dedupeShops = (shops: Shop[]): Shop[] => {
  const seen = new Set<string>();

  return shops.filter((shop) => {
    if (!shop?.id || seen.has(shop.id)) {
      return false;
    }

    seen.add(shop.id);
    return true;
  });
};

const toShop = (raw: any): Shop | null => {
  if (!raw?.id) {
    return null;
  }

  return {
    id: String(raw.id),
    name: String(raw.name || raw.shopName || ''),
    timezone: String(raw.timezone || DEFAULT_TIMEZONE),
  };
};

interface SearchableRequesterSelectProps {
  requesters: TimeOffRequester[];
  value: string;
  onChange: (requesterKey: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  emptyLabel?: string;
}

function SearchableRequesterSelect({
  requesters,
  value,
  onChange,
  placeholder = 'Rechercher une personne…',
  disabled = false,
  required = false,
  emptyLabel = 'Aucune personne trouvée',
}: SearchableRequesterSelectProps) {
  const selectedRequester =
    requesters.find((requester) => requester.key === value) || null;

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const normalizedSearch = search.trim().toLocaleLowerCase('fr-FR');

  const filteredRequesters = requesters.filter((requester) => {
    if (!normalizedSearch) {
      return true;
    }

    const name = requester.name.toLocaleLowerCase('fr-FR');

    const reverseName = requester.name
      .split(/\s+/)
      .reverse()
      .join(' ')
      .toLocaleLowerCase('fr-FR');

    const roleLabel = formatRequesterRole(
      requester.type,
      requester.role,
    ).toLocaleLowerCase('fr-FR');

    return (
      name.includes(normalizedSearch) ||
      reverseName.includes(normalizedSearch) ||
      roleLabel.includes(normalizedSearch)
    );
  });

  const displayValue = open
    ? search
    : selectedRequester?.name || '';

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

        <input
          type="text"
          value={displayValue}
          onFocus={() => {
            setOpen(true);
            setSearch('');
          }}
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);

            if (value) {
              onChange('');
            }
          }}
          onBlur={() => {
            /*
             * Le léger délai permet au clic sur une option
             * d'être traité avant de refermer la liste.
             */
            window.setTimeout(() => setOpen(false), 120);
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required && !value}
          autoComplete="off"
          className="input-field w-full pl-10"
        />

        {value && !disabled && (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange('');
              setSearch('');
              setOpen(true);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Effacer le demandeur"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-[10050] mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {filteredRequesters.length > 0 ? (
            filteredRequesters.map((requester) => {
              const isSelected = requester.key === value;
              const roleLabel = formatRequesterRole(
                requester.type,
                requester.role,
              );

              return (
                <button
                  key={requester.key}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(requester.key);
                    setSearch('');
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    isSelected
                      ? 'bg-primary-50 font-medium text-primary-700'
                      : 'text-gray-700'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{requester.name}</span>

                    {roleLabel && (
                      <span className="block truncate text-xs capitalize text-gray-500">
                        {roleLabel}
                      </span>
                    )}
                  </span>

                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-3 text-sm text-gray-500">{emptyLabel}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function TimeOffManagerPage() {
  const { t } = useTranslation();
  const { showToast, ToastComponent } = useToast();

  const { user } = useSelector((state: RootState) => state.auth);

  const { currentShop } = useCurrentShop();

  const outletContext = useOutletContext<LayoutOutletContext | undefined>();

  const unlockedBy = outletContext?.unlockedBy ?? null;

  /*
   * =========================================================
   * ACTEUR EFFECTIF
   * =========================================================
   *
   * Si quelqu'un a déverrouillé la session avec son code staff,
   * c'est cette personne qui devient le demandeur effectif,
   * sans remplacer la session d'authentification.
   *
   * Sinon, on utilise le propriétaire de la session.
   */

  const sessionUserType = normalizeKey((user as any)?.userType);
  const sessionRole = normalizeKey((user as any)?.role);

  const effectiveRole = normalizeKey(unlockedBy?.role || sessionRole);

  const effectiveRequesterType = normalizeKey(
    unlockedBy?.type ||
      sessionUserType ||
      (sessionRole ? 'ADMIN' : ''),
  );

  /*
   * Admin et Super Admin peuvent créer une demande pour
   * quelqu'un d'autre. Tous les autres rôles ne peuvent
   * demander que pour eux-mêmes.
   */
  const isAdmin =
    effectiveRole === 'ADMIN' || effectiveRole === 'SUPER_ADMIN';

  /*
   * Seul le Super Admin peut approuver et voir tous les shops.
   */
  const isSuperAdmin = effectiveRole === 'SUPER_ADMIN';

  /*
   * Les endpoints de liste du staff sont réservés aux comptes admin.
   */
  const canBrowseRequesters = sessionUserType === 'ADMIN';

  const effectiveRequesterId = String(
    unlockedBy?.id || (user as any)?.id || '',
  );

  const effectiveRequesterName =
    unlockedBy?.name ||
    (user as any)?.name ||
    (user as any)?.email ||
    'Utilisateur';

  const effectivePractitionerId = String(
    unlockedBy?.practitionerId ||
      (sessionUserType === 'PRACTITIONER' ? (user as any)?.id : '') ||
      '',
  );

  const effectiveEmployeeId = String(
    unlockedBy?.employeeId ||
      (!unlockedBy && sessionUserType === 'EMPLOYEE'
        ? (user as any)?.id
        : '') ||
      '',
  );

  const effectiveShopId = String(
    unlockedBy?.shopId || currentShop?.id || (user as any)?.shopId || '',
  );

  /*
   * =========================================================
   * STATE
   * =========================================================
   */

  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);

  const [requesters, setRequesters] = useState<TimeOffRequester[]>([]);

  const [filterShops, setFilterShops] = useState<Shop[]>([]);

  const [requestShops, setRequestShops] = useState<Shop[]>([]);

  const [loadingFilterShops, setLoadingFilterShops] = useState(false);

  const [loadingRequestShops, setLoadingRequestShops] = useState(false);

  const [loading, setLoading] = useState(false);

  const [isCreating, setIsCreating] = useState(false);

  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(
    null,
  );

  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(
    null,
  );

  const [requestToDelete, setRequestToDelete] =
    useState<TimeOffRequest | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [filters, setFilters] = useState({
    requesterKey: '',
    shopId: '',
    status: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const [newRequest, setNewRequest] = useState({
    requesterKey: '',
    shopId: '',
    startDateTime: '',
    endDateTime: '',
    reason: '',
    timezone: DEFAULT_TIMEZONE,
  });

  /*
   * Annuaire des shops, utilisé uniquement pour retrouver le nom
   * des shops déjà rattachés à une personne.
   */
  const shopDirectoryRef = useRef<Shop[] | null>(null);

  const filterRequester = useMemo(
    () =>
      requesters.find(
        (requester) => requester.key === filters.requesterKey,
      ) || null,
    [requesters, filters.requesterKey],
  );

  const selectedRequester = useMemo(
    () =>
      requesters.find(
        (requester) => requester.key === newRequest.requesterKey,
      ) || null,
    [requesters, newRequest.requesterKey],
  );

  /*
   * Les anciennes demandes ne possèdent pas de requesterId :
   * on filtre donc en priorité sur la référence métier.
   */
  const filterRequesterParam = useMemo(() => {
    if (!filterRequester) {
      return null;
    }

    if (filterRequester.practitionerId) {
      return {
        name: 'practitionerId',
        value: filterRequester.practitionerId,
      };
    }

    if (filterRequester.employeeId) {
      return {
        name: 'employeeId',
        value: filterRequester.employeeId,
      };
    }

    return { name: 'requesterId', value: filterRequester.id };
  }, [filterRequester]);

  useEffect(() => {
    if (!isSuperAdmin && filters.shopId) {
      setFilters((prev) => ({ ...prev, shopId: '' }));
    }
  }, [isSuperAdmin, filters.shopId]);

  /*
   * =========================================================
   * LOAD DATA
   * =========================================================
   */

  useEffect(() => {
    void loadTimeOffRequests();
  }, [
    filterRequesterParam,
    filters.shopId,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    effectiveShopId,
    isSuperAdmin,
  ]);

  useEffect(() => {
    if (!canBrowseRequesters) {
      return;
    }

    void loadRequesters();
  }, [canBrowseRequesters]);

  /*
   * Pour un utilisateur qui demande pour lui-même, on résout ses
   * propres shops afin de récupérer le nom et la timezone.
   */
  useEffect(() => {
    if (isAdmin) {
      return;
    }

    void loadOwnShops();
  }, [
    isAdmin,
    effectivePractitionerId,
    effectiveRequesterId,
    effectiveRequesterType,
  ]);

  const fetchData = async (url: string): Promise<any | null> => {
    const response = await apiClient(url);

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      return null;
    }

    return data.data ?? null;
  };

  const loadTimeOffRequests = async () => {
    /*
     * VISIBILITÉ DES CONGÉS
     *
     * - SUPER_ADMIN : tous les shops, filtre shop disponible.
     * - Tous les autres rôles : uniquement le shop effectif.
     *
     * Le shopId est imposé ici et ne dépend jamais du filtre UI.
     * Le contrôle principal reste côté backend.
     */
    if (!isSuperAdmin && !effectiveShopId) {
      console.warn(
        'Impossible de charger les congés : aucun shop effectif pour cet utilisateur.',
      );

      setTimeOffRequests([]);
      return;
    }

    setLoading(true);

    try {
      const queryParams = new URLSearchParams();

      if (isSuperAdmin) {
        if (filters.shopId) {
          queryParams.append('shopId', filters.shopId);
        }
      } else {
        queryParams.append('shopId', effectiveShopId);
      }

      if (filterRequesterParam) {
        queryParams.append(
          filterRequesterParam.name,
          filterRequesterParam.value,
        );
      }

      if (filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }

      if (filters.dateFrom) {
        queryParams.append('dateFrom', filters.dateFrom);
      }

      if (filters.dateTo) {
        queryParams.append('dateTo', filters.dateTo);
      }

      const queryString = queryParams.toString();

      const url = queryString
        ? `/api/planning/time-off?${queryString}`
        : '/api/planning/time-off';

      const data = await fetchData(url);

      if (!data) {
        throw new Error('Failed to load time-off requests');
      }

      let requests: TimeOffRequest[] = data.timeOffRequests || [];

      /*
       * Sécurité visuelle supplémentaire : un utilisateur non
       * super-admin ne voit jamais les congés d'un autre shop.
       */
      if (!isSuperAdmin) {
        requests = requests.filter(
          (request) => request.shopId === effectiveShopId,
        );
      }

      setTimeOffRequests(requests);
    } catch (error) {
      console.error('Failed to load time-off requests:', error);

      setTimeOffRequests([]);
    } finally {
      setLoading(false);
    }
  };

  /*
   * Liste commune des personnes pouvant demander un congé.
   *
   * Chaque source est optionnelle : si un endpoint n'est pas
   * autorisé pour le rôle courant, il est simplement ignoré.
   */
  const loadRequesters = async () => {
    const [practitioners, employees, vendors, admins] = await Promise.all([
      fetchData('/api/admin/practitioners?limit=1000').catch(() => null),
      fetchData('/api/admin/employees?limit=1000').catch(() => null),
      fetchData('/api/admin/vendors?limit=1000').catch(() => null),
      fetchData('/api/admin/admins?limit=1000').catch(() => null),
    ]);

    const list: TimeOffRequester[] = [];

    for (const practitioner of practitioners?.practitioners || []) {
      const name =
        `${practitioner.firstName || ''} ${practitioner.lastName || ''}`.trim() ||
        practitioner.artistName ||
        practitioner.email ||
        'Praticien';

      list.push({
        key: `PRACTITIONER:${practitioner.id}`,
        id: String(practitioner.id),
        name,
        type: 'PRACTITIONER',
        role: 'PRACTITIONER',
        practitionerId: String(practitioner.id),
      });
    }

    for (const employee of employees?.employees || []) {
      const person = employee.person || {};

      const name =
        String(person.displayName || '').trim() ||
        `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
        person.email ||
        'Employé';

      const shops = dedupeShops(
        [
          toShop(employee.shop),
          ...(employee.employeeShops || []).map((link: any) =>
            toShop(link?.shop),
          ),
        ].filter(Boolean) as Shop[],
      );

      list.push({
        key: `EMPLOYEE:${employee.id}`,
        id: String(employee.id),
        name,
        type: 'EMPLOYEE',
        role: employee.type || 'EMPLOYEE',
        employeeId: String(employee.id),
        shops,
      });
    }

    for (const vendor of vendors?.vendors || []) {
      const person = vendor.person || {};

      const name =
        String(person.displayName || '').trim() ||
        `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
        person.email ||
        'Vendeur';

      const shops = dedupeShops(
        [toShop(vendor.shop)].filter(Boolean) as Shop[],
      );

      list.push({
        key: `VENDOR:${vendor.id}`,
        id: String(vendor.id),
        name,
        type: 'VENDOR',
        role: 'VENDOR',
        shops,
      });
    }

    for (const admin of admins?.admins || []) {
      list.push({
        key: `ADMIN:${admin.id}`,
        id: String(admin.id),
        name: String(admin.name || admin.email || 'Administrateur'),
        type: 'ADMIN',
        role: admin.role || 'ADMIN',
      });
    }

    list.sort((a, b) => a.name.localeCompare(b.name, 'fr-FR'));

    setRequesters(list);
  };

  const loadShopDirectory = async (): Promise<Shop[]> => {
    if (shopDirectoryRef.current) {
      return shopDirectoryRef.current;
    }

    const data = await fetchData(
      '/api/admin/shops?allShops=true&limit=1000',
    ).catch(() => null);

    const shops = dedupeShops(
      (data?.shops || [])
        .map((shop: any) => toShop(shop))
        .filter(Boolean) as Shop[],
    );

    shopDirectoryRef.current = shops;

    return shops;
  };

  const fetchPractitionerShops = async (
    practitionerId: string,
  ): Promise<Shop[]> => {
    const data = await fetchData(
      `/api/admin/practitioners/${encodeURIComponent(practitionerId)}/shops`,
    ).catch(() => null);

    return dedupeShops(
      (data?.shops || [])
        .filter((shop: any) => shop?.isActive !== false)
        .map((shop: any) =>
          toShop({
            id: shop.shopId,
            name: shop.shopName,
            timezone: shop.timezone,
          }),
        )
        .filter(Boolean) as Shop[],
    );
  };

  const fetchAdminShops = async (adminId: string): Promise<Shop[]> => {
    const data = await fetchData(
      `/api/admin/admins/${encodeURIComponent(adminId)}`,
    ).catch(() => null);

    const assignedShopIds: string[] = (
      data?.admin?.assignedShopIds || []
    ).map((shopId: any) => String(shopId));

    if (assignedShopIds.length === 0) {
      return [];
    }

    const directory = await loadShopDirectory();

    return directory.filter((shop) => assignedShopIds.includes(shop.id));
  };

  /*
   * Shops réellement liés à une personne, quel que soit son type.
   * On ne propose jamais tous les shops du système.
   */
  const resolveRequesterShops = async (
    requester: Pick<
      TimeOffRequester,
      'id' | 'type' | 'practitionerId' | 'shops'
    >,
  ): Promise<Shop[]> => {
    if (requester.shops && requester.shops.length > 0) {
      return requester.shops;
    }

    if (requester.practitionerId) {
      return fetchPractitionerShops(requester.practitionerId);
    }

    const type = normalizeKey(requester.type);

    if (
      type === 'ADMIN' ||
      type === 'SUPER_ADMIN' ||
      type === 'MANAGER' ||
      type === 'COMPANY_ADMIN' ||
      type === 'SHOP_ADMIN'
    ) {
      return fetchAdminShops(requester.id);
    }

    return [];
  };

  const loadRequesterShops = async (
    requester: TimeOffRequester | null,
    target: 'filter' | 'request',
  ): Promise<Shop[]> => {
    const setTargetShops =
      target === 'filter' ? setFilterShops : setRequestShops;

    const setTargetLoading =
      target === 'filter' ? setLoadingFilterShops : setLoadingRequestShops;

    if (!requester) {
      setTargetShops([]);
      return [];
    }

    setTargetLoading(true);

    try {
      const shops = await resolveRequesterShops(requester);

      setTargetShops(shops);

      return shops;
    } catch (error) {
      console.error('Failed to load requester shops:', error);

      setTargetShops([]);
      return [];
    } finally {
      setTargetLoading(false);
    }
  };

  /*
   * Shops de l'acteur effectif lorsqu'il demande pour lui-même.
   * Le shop reste celui de la session : on ne cherche ici que
   * le nom et la timezone.
   */
  const loadOwnShops = async () => {
    if (!effectiveRequesterId && !effectivePractitionerId) {
      return;
    }

    setLoadingRequestShops(true);

    try {
      const shops = await resolveRequesterShops({
        id: effectiveRequesterId,
        type: effectiveRequesterType,
        practitionerId: effectivePractitionerId || null,
      });

      setRequestShops(shops);
    } catch (error) {
      console.error('Failed to load effective user shops:', error);

      setRequestShops([]);
    } finally {
      setLoadingRequestShops(false);
    }
  };

  const handleRequesterFilterChange = async (requesterKey: string) => {
    setFilters((prev) => ({
      ...prev,
      requesterKey,
      shopId: '',
    }));

    const requester =
      requesters.find((candidate) => candidate.key === requesterKey) || null;

    const shops = await loadRequesterShops(requester, 'filter');

    /*
     * Seul le super admin peut filtrer librement par shop.
     * S'il n'y en a qu'un, on le présélectionne.
     */
    if (isSuperAdmin && shops.length === 1) {
      setFilters((prev) =>
        prev.requesterKey === requesterKey
          ? { ...prev, shopId: shops[0].id }
          : prev,
      );
    }
  };

  const handleRequestRequesterChange = async (requesterKey: string) => {
    setNewRequest((prev) => ({
      ...prev,
      requesterKey,
      shopId: '',
      timezone: DEFAULT_TIMEZONE,
    }));

    const requester =
      requesters.find((candidate) => candidate.key === requesterKey) || null;

    const shops = await loadRequesterShops(requester, 'request');

    /*
     * Un seul shop lié : sélection automatique.
     * Plusieurs shops : la personne choisit.
     */
    if (shops.length === 1) {
      setNewRequest((prev) =>
        prev.requesterKey === requesterKey
          ? {
              ...prev,
              shopId: shops[0].id,
              timezone: shops[0].timezone || DEFAULT_TIMEZONE,
            }
          : prev,
      );
    }
  };

  /*
   * =========================================================
   * DATE HELPERS
   * =========================================================
   */

  const formatLocalDateTimeToIso = (dateTimeLocal: string): string => {
    if (!dateTimeLocal) {
      return '';
    }

    return new Date(dateTimeLocal).toISOString();
  };

  const formatDateTime = (dateTime: string, timezone: string) =>
    new Date(dateTime).toLocaleString('fr-FR', {
      timeZone: timezone || DEFAULT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  /*
   * =========================================================
   * CREATION DE LA DEMANDE
   * =========================================================
   */

  const handleCreateRequest = async () => {
    try {
      let requesterId = '';
      let requesterType = '';
      let requesterName = '';
      let requesterRole = '';
      let practitionerId = '';
      let employeeId = '';
      let shopId = '';
      let timezone = DEFAULT_TIMEZONE;

      if (isAdmin) {
        /*
         * -------------------------------------------------------
         * ADMIN / SUPER ADMIN
         * -------------------------------------------------------
         *
         * Ils peuvent créer une demande pour n'importe quelle
         * personne autorisée, dans l'un de ses shops.
         */
        if (!selectedRequester) {
          showToast('Veuillez sélectionner un demandeur.', 'warning');
          return;
        }

        shopId = newRequest.shopId;

        if (!shopId) {
          showToast('Veuillez sélectionner un shop.', 'warning');
          return;
        }

        const selectedShop = requestShops.find((shop) => shop.id === shopId);

        if (!selectedShop) {
          showToast(
            "Le shop sélectionné n'est pas lié à cette personne.",
            'warning',
          );
          return;
        }

        requesterId = selectedRequester.id;
        requesterType = normalizeKey(selectedRequester.type);
        requesterName = selectedRequester.name;
        requesterRole = normalizeKey(
          selectedRequester.role || selectedRequester.type,
        );
        practitionerId = String(selectedRequester.practitionerId || '');
        employeeId = String(selectedRequester.employeeId || '');

        timezone =
          selectedShop.timezone || newRequest.timezone || DEFAULT_TIMEZONE;
      } else {
        /*
         * -------------------------------------------------------
         * TOUS LES AUTRES RÔLES
         * -------------------------------------------------------
         *
         * La demande appartient à l'acteur effectif : personne
         * à sélectionner, shop imposé par la session.
         *
         * Aucun practitionerId ni employeeId n'est exigé.
         */
        requesterId = effectiveRequesterId;
        requesterType = effectiveRequesterType;
        requesterName = effectiveRequesterName;
        requesterRole = effectiveRole || effectiveRequesterType;
        practitionerId = effectivePractitionerId;
        employeeId = effectiveEmployeeId;
        shopId = effectiveShopId;

        if (!requesterId) {
          console.error('No effective requester found:', {
            unlockedBy,
            user,
          });

          showToast(
            'Impossible d’identifier votre profil pour créer une demande de congé.',
            'error',
          );

          return;
        }

        if (!shopId) {
          console.error('No shopId found for effective requester:', {
            unlockedBy,
            currentShop,
          });

          showToast('Impossible de déterminer votre shop.', 'error');

          return;
        }

        timezone =
          requestShops.find((shop) => shop.id === shopId)?.timezone ||
          DEFAULT_TIMEZONE;
      }

      if (!newRequest.reason || newRequest.reason.trim().length === 0) {
        showToast(
          t('planning.timeOff.form.reasonRequired') ||
            'Le motif est obligatoire.',
          'warning',
        );

        return;
      }

      const startDateTime = formatLocalDateTimeToIso(
        newRequest.startDateTime,
      );

      const endDateTime = formatLocalDateTimeToIso(newRequest.endDateTime);

      if (!startDateTime || !endDateTime) {
        showToast(
          'Les dates de début et de fin sont obligatoires.',
          'warning',
        );

        return;
      }

      if (
        new Date(endDateTime).getTime() <= new Date(startDateTime).getTime()
      ) {
        showToast(
          'La date de fin doit être postérieure à la date de début.',
          'warning',
        );

        return;
      }

      /*
       * -------------------------------------------------------
       * PAYLOAD
       * -------------------------------------------------------
       *
       * requesterId est l'identité principale du demandeur.
       * practitionerId / employeeId ne sont envoyés que
       * lorsqu'ils existent réellement.
       */
      const requestData = {
        requesterId,
        requesterType,
        requesterName,

        ...(requesterRole ? { requesterRole } : {}),

        shopId,
        startDateTime,
        endDateTime,
        reason: newRequest.reason.trim(),
        timezone,

        ...(practitionerId ? { practitionerId } : {}),
        ...(employeeId ? { employeeId } : {}),

        /*
         * Acteur ayant déverrouillé la session : conservé pour
         * que le backend puisse recouper l'identité effective.
         */
        ...(unlockedBy
          ? {
              unlockedByActorId: unlockedBy.id,
              unlockedByName: unlockedBy.name,
              unlockedByRole: unlockedBy.role,
              unlockedByType: unlockedBy.type,
              unlockedByShopId: unlockedBy.shopId,
            }
          : {}),
      };

      setIsCreating(true);

      const response = await apiClient('/api/planning/time-off', {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        console.error('Time-off create error:', data);

        showToast(
          data?.message || 'Impossible de créer la demande de congé.',
          'error',
        );

        return;
      }

      setShowCreateModal(false);

      setNewRequest({
        requesterKey: '',
        shopId: '',
        startDateTime: '',
        endDateTime: '',
        reason: '',
        timezone: DEFAULT_TIMEZONE,
      });

      if (isAdmin) {
        setRequestShops([]);
      }

      await loadTimeOffRequests();
      showToast('La demande de congé a été créée.', 'success');
    } catch (error) {
      console.error('Failed to create time-off request:', error);

      showToast('Impossible de créer la demande de congé.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  /*
   * =========================================================
   * APPROBATION
   * =========================================================
   */

  const handleApproveRequest = async (requestId: string) => {
    if (!isSuperAdmin) {
      showToast(
        'Seul le Super Admin peut approuver une demande de congé.',
        'warning',
      );

      return;
    }

    setApprovingRequestId(requestId);

    try {
      const response = await apiClient(
        `/api/planning/time-off/${requestId}/approve`,
        { method: 'PATCH' },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        showToast(
          data?.message || 'Impossible d’approuver la demande de congé.',
          'error',
        );

        return;
      }

      await loadTimeOffRequests();
      showToast('La demande de congé a été approuvée.', 'success');
    } catch (error) {
      console.error('Failed to approve time-off request:', error);

      showToast('Impossible d’approuver la demande de congé.', 'error');
    } finally {
      setApprovingRequestId(null);
    }
  };

  /*
   * =========================================================
   * DELETE
   * =========================================================
   */

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;

    const requestId = requestToDelete.id;
    setDeletingRequestId(requestId);

    try {
      const response = await apiClient(
        `/api/planning/time-off/${requestId}`,
        { method: 'DELETE' },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        showToast(
          data?.message || 'Impossible de supprimer la demande de congé.',
          'error',
        );

        return;
      }

      await loadTimeOffRequests();
      setRequestToDelete(null);
      showToast('La demande de congé a été supprimée.', 'success');
    } catch (error) {
      console.error('Failed to delete time-off request:', error);

      showToast('Impossible de supprimer la demande de congé.', 'error');
    } finally {
      setDeletingRequestId(null);
    }
  };

  /*
   * =========================================================
   * STATUS
   * =========================================================
   */

  const getStatusBadge = (isApproved: boolean) => {
    if (isApproved) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Approuvée
        </span>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
        En attente
      </span>
    );
  };

  /*
   * =========================================================
   * SHOP EFFECTIF POUR AFFICHAGE
   * =========================================================
   */

  const effectiveShopName =
    requestShops.find((shop) => shop.id === effectiveShopId)?.name ||
    currentShop?.name ||
    'Shop courant';

  const effectiveRoleLabel = formatRequesterRole(
    effectiveRequesterType,
    effectiveRole,
  );

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="dt-list-toolbar flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('planning.timeOff.title')}
          </h1>

          <p className="text-gray-600">{t('planning.timeOff.subtitle')}</p>
        </div>

        <button
          type="button"
          onClick={() => {
            setShowCreateModal(true);

            if (!isAdmin) {
              void loadOwnShops();
            }
          }}
          className="btn-primary inline-flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          {t('planning.timeOff.createRequest')}
        </button>
      </div>

      {/* ACTEUR EFFECTIF */}
      {unlockedBy && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-600">
            Connecté en tant que
          </p>

          <p className="mt-0.5 text-sm font-semibold text-blue-900">
            {unlockedBy.name}
            {effectiveRoleLabel && (
              <span className="ml-2 text-xs font-normal capitalize text-blue-700">
                {effectiveRoleLabel}
              </span>
            )}
          </p>

          {!isAdmin && (
            <p className="mt-1 text-xs text-blue-700">
              Les demandes de congé seront automatiquement créées pour cette
              personne et son shop.
            </p>
          )}
        </div>
      )}

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* REQUESTER FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.timeOff.filters.requester')}
            </label>

            {canBrowseRequesters ? (
              <SearchableRequesterSelect
                requesters={requesters}
                value={filters.requesterKey}
                onChange={(requesterKey) =>
                  void handleRequesterFilterChange(requesterKey)
                }
                placeholder="Rechercher un demandeur…"
              />
            ) : (
              <div className="input-field flex items-center bg-gray-50 text-gray-700">
                {effectiveRequesterName}
              </div>
            )}
          </div>

          {/* SHOP FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.timeOff.filters.shop')}
            </label>

            {isSuperAdmin ? (
              <select
                value={filters.shopId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, shopId: e.target.value }))
                }
                disabled={!filters.requesterKey || loadingFilterShops}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">
                  {!filters.requesterKey
                    ? 'Sélectionnez d’abord un demandeur'
                    : loadingFilterShops
                      ? 'Chargement des shops…'
                      : t('planning.timeOff.filters.allShops')}
                </option>

                {filterShops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="input-field flex items-center bg-gray-50 text-gray-700">
                {effectiveShopName}
              </div>
            )}
          </div>

          {/* STATUS FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.timeOff.filters.status')}
            </label>

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">
                {t('planning.timeOff.filters.allStatuses')}
              </option>

              <option value="pending">
                {t('planning.timeOff.filters.pending')}
              </option>

              <option value="approved">
                {t('planning.timeOff.filters.approved')}
              </option>
            </select>
          </div>

          {/* DATE FROM */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.timeOff.filters.dateFrom')}
            </label>

            <DateInput
              value={filters.dateFrom}
              onChange={(value) =>
                setFilters({ ...filters, dateFrom: value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* DATE TO */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('planning.timeOff.filters.dateTo')}
            </label>

            <DateInput
              value={filters.dateTo}
              onChange={(value) => setFilters({ ...filters, dateTo: value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* REQUESTS TABLE */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {t('planning.timeOff.requests')} ({timeOffRequests.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.requester')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.shop')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.period')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.reason')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.status')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('planning.timeOff.table.actions')}
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {timeOffRequests.map((request) => {
                  const roleLabel = formatRequesterRole(
                    request.requesterType,
                    request.requesterRole,
                  );

                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {requestRequesterName(request)}
                        </div>

                        {roleLabel && (
                          <div className="text-xs capitalize text-gray-500">
                            {roleLabel}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {request.shopName}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(
                            request.startDateTime,
                            request.timezone,
                          )}
                        </div>

                        <div className="text-sm text-gray-500">
                          {formatDateTime(
                            request.endDateTime,
                            request.timezone,
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {request.reason}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.isApproved)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {isSuperAdmin && !request.isApproved && (
                            <button
                              type="button"
                              onClick={() =>
                                handleApproveRequest(request.id)
                              }
                              disabled={approvingRequestId === request.id}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {approvingRequestId === request.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                              {t('planning.timeOff.approve')}
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => setRequestToDelete(request)}
                            disabled={deletingRequestId === request.id}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deletingRequestId === request.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                            {t('planning.timeOff.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div
          className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-gray-900/60 p-4 backdrop-blur-[1px] ${isCreating ? 'cursor-wait' : ''}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isCreating) {
              setShowCreateModal(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="time-off-modal-title"
            className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <CalendarOff className="h-5 w-5" />
                </div>
                <div>
                  <h3
                    id="time-off-modal-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    {t('planning.timeOff.createRequest')}
                  </h3>
                  <p className="mt-0.5 text-sm text-gray-500">
                    Renseignez la période et le motif de l’absence.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
                aria-label="Fermer"
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 overflow-y-auto px-6 py-5">
              {/* REQUESTER */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.timeOff.form.applicant')} *
                </label>

                {isAdmin ? (
                  <SearchableRequesterSelect
                    requesters={requesters}
                    value={newRequest.requesterKey}
                    onChange={(requesterKey) =>
                      void handleRequestRequesterChange(requesterKey)
                    }
                    placeholder="Rechercher un demandeur…"
                    required
                  />
                ) : (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                    <p className="font-semibold text-gray-900">
                      {effectiveRequesterName}
                    </p>

                    {effectiveRoleLabel && (
                      <p className="text-xs capitalize text-gray-500">
                        {effectiveRoleLabel}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* SHOP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.timeOff.form.shop')} *
                </label>

                {isAdmin ? (
                  <>
                    <select
                      value={newRequest.shopId}
                      onChange={(e) => {
                        const selectedShop = requestShops.find(
                          (shop) => shop.id === e.target.value,
                        );

                        setNewRequest({
                          ...newRequest,
                          shopId: e.target.value,
                          timezone:
                            selectedShop?.timezone || DEFAULT_TIMEZONE,
                        });
                      }}
                      className="input-field"
                      required
                      disabled={
                        !newRequest.requesterKey || loadingRequestShops
                      }
                    >
                      <option value="">
                        {!newRequest.requesterKey
                          ? 'Sélectionnez d’abord un demandeur'
                          : loadingRequestShops
                            ? 'Chargement des shops…'
                            : t('planning.timeOff.form.selectShop')}
                      </option>

                      {requestShops.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.name}
                        </option>
                      ))}
                    </select>

                    {newRequest.requesterKey &&
                      !loadingRequestShops &&
                      requestShops.length === 0 && (
                        <p className="mt-2 text-sm text-amber-600">
                          Cette personne n’est associée à aucun shop actif.
                        </p>
                      )}
                  </>
                ) : (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                    <p className="font-semibold text-gray-900">
                      {effectiveShopName}
                    </p>
                  </div>
                )}
              </div>

              {/* START */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.timeOff.form.startDateTime')}
                </label>

                <DateTimeInput
                  value={newRequest.startDateTime}
                  onChange={(value) =>
                    setNewRequest({ ...newRequest, startDateTime: value })
                  }
                  className="input-field"
                  required
                />
              </div>

              {/* END */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.timeOff.form.endDateTime')}
                </label>

                <DateTimeInput
                  value={newRequest.endDateTime}
                  onChange={(value) =>
                    setNewRequest({ ...newRequest, endDateTime: value })
                  }
                  className="input-field"
                  required
                />
              </div>

              {/* REASON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('planning.timeOff.form.reason')}
                </label>

                <textarea
                  value={newRequest.reason}
                  onChange={(e) =>
                    setNewRequest({ ...newRequest, reason: e.target.value })
                  }
                  className="input-field resize-none"
                  rows={3}
                  required
                />
              </div>

              {!isAdmin && (
                <div className="rounded-md bg-blue-50 px-3 py-3 text-sm text-blue-800">
                  Cette demande sera créée pour{' '}
                  <strong>{effectiveRequesterName}</strong> dans le shop{' '}
                  <strong>{effectiveShopName}</strong>.
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>

              <button
                type="button"
                onClick={handleCreateRequest}
                disabled={isCreating}
                className="btn-primary inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Création…
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    {t('planning.timeOff.create')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(requestToDelete)}
        title="Supprimer la demande de congé"
        message={
          requestToDelete
            ? `La demande de ${requestRequesterName(requestToDelete)} sera définitivement supprimée.`
            : ''
        }
        confirmLabel="Supprimer"
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        isLoading={Boolean(deletingRequestId)}
        onConfirm={() => void handleDeleteRequest()}
        onClose={() => setRequestToDelete(null)}
      />

      <ToastComponent />
    </div>
  );
}
